import { computed, Injectable, inject, signal } from "@angular/core";
import { NavigationEnd } from "@angular/router";
import { format, parseISO } from "date-fns";
import { BehaviorSubject, catchError, map, Observable, of } from "rxjs";
import { IApiResponse } from "../../models/api-response.model";
import { Country, CountryListResponse } from "../../models/country.model";
import { ICategory } from "../../models/product-list.model";
import { ISessionUser } from "../../models/session.model";
import { useSubject } from "../../utils";
import { RequestService } from "../request/request.service";
@Injectable({
  providedIn: "root",
})
export class AppStateService {
  windowSize$ = new BehaviorSubject<{
    innerWidth: number;
    innerHeight: number;
  }>(null as any);
  deviceType$ = new BehaviorSubject<"desktop" | "mobile">(null as any);
  routeNavigationEnd$ = useSubject<NavigationEnd>(null as any);
  /**
   * Truthy (set to the user's `access` level) once a session is confirmed via
   * `checkSession()`, empty string otherwise. Session state now lives in the
   * httpOnly `ab_session` cookie — this is just a client-side mirror of "are
   * we currently authenticated", established by calling `/api/v1/auth/session`,
   * never by reading localStorage.
   */
  userToken$ = new BehaviorSubject<string>("");
  /** Current user's profile, hydrated from `/api/v1/auth/session`. */
  currentUser$ = new BehaviorSubject<ISessionUser | null>(null);

  parentCategories = signal<ICategory[]>([]);
  allCountries = signal<Country[]>([]);
  /**
   * Markets as the admin portal works with them: open for listing or live
   * (Country > Market launch). A market being prepared before launch shows up
   * here so its products, banners, tags and delivery can be set up ahead.
   */
  destinationCountries = computed(() =>
    this.allCountries().filter((country) => country.listing_enabled || country.destination_enabled)
  );
  /** Live markets only: shoppers can see them. Dashboards and outreach use these. */
  liveCountries = computed(() =>
    this.allCountries().filter((country) => country.destination_enabled)
  );
  /** Live market codes, upper-case; SG and US until the country list has loaded. */
  liveMarketCodes = computed(() => {
    const codes = this.liveCountries().map((c) => c.iso2.toUpperCase());
    return codes.length ? codes : ["SG", "US"];
  });

  private readonly request = inject(RequestService);
  private countriesLoading = false;

  /**
   * Lazily load the country list if it isn't populated yet.
   *
   * Countries are normally fetched once at app bootstrap, but that call requires
   * an admin token — when the app boots at the login screen (or with an expired
   * token) it fails and was never retried, leaving every country dropdown empty
   * for the rest of the session. Screens that depend on the list call this in
   * ngOnInit so they self-heal after login. No-ops when already loaded/in-flight.
   */
  ensureCountriesLoaded(): void {
    if (this.allCountries().length > 0 || this.countriesLoading) return;
    this.refreshCountries();
  }

  /**
   * Re-fetch the country list even when it is loaded - after a market launch
   * stage change, say, so every market dropdown and `liveCountries()` follow.
   */
  refreshCountries(): void {
    this.countriesLoading = true;
    this.request.get<CountryListResponse>("/api/v2/country").subscribe({
      next: (res) => {
        if (res?.status === true && Array.isArray(res.data)) {
          this.allCountries.set(res.data);
        }
        this.countriesLoading = false;
      },
      error: () => {
        this.countriesLoading = false;
      },
    });
  }
  /**
   * Validates the current session against the `ab_session` cookie and
   * updates `userToken$`/`currentUser$` accordingly. Pure — never navigates —
   * so it's safe to call from app bootstrap, `AuthGaurd`, and the refresh
   * button alike. Resolves `true`/`false`, never throws.
   */
  checkSession(): Observable<boolean> {
    return this.request.get<IApiResponse<ISessionUser>>("/api/v1/auth/session").pipe(
      map((res) => {
        if (res?.status === true && res.data) {
          this.currentUser$.next(res.data);
          this.userToken$.next(res.data.access || "authenticated");
          return true;
        }
        this.currentUser$.next(null);
        this.userToken$.next("");
        return false;
      }),
      catchError(() => {
        this.currentUser$.next(null);
        this.userToken$.next("");
        return of(false);
      })
    );
  }

  /** Called once at app startup (APP_INITIALIZER) to hydrate auth state from the `ab_session` cookie. */
  bootstrapSession(): Observable<boolean> {
    return this.checkSession();
  }

  /**
   * Shared no-reload refresh: re-validates the session, then either runs the
   * page's own refetch (`refetchFn`) or hard-reloads to `/login` if the
   * session is gone — mirrors the interceptor's expired-session handling,
   * since an httpOnly cookie can't be inspected/cleared from JS so a full
   * re-bootstrap is the reliable way to resync state.
   */
  refreshCurrentView(refetchFn: () => void): void {
    this.checkSession().subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        refetchFn();
      } else {
        window.location.href = "/login";
      }
    });
  }

  getFormattedDateFromISO({
    isoDateStr,
    dateFormat,
  }: {
    isoDateStr: string;
    dateFormat: "yyyy-MM-dd HH:mm a";
  }): string {
    return format(parseISO(isoDateStr), dateFormat);
  }

  apiLoading$ = new BehaviorSubject<boolean>(false);
}
